import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { ConnectionState } from '../App';
import { socket } from '../App';
import { CheckCircle, MessageCircle, Smartphone, Loader2, RefreshCw, LogOut } from 'lucide-react';
import { WhatsAppContacts, WhatsAppContact, WhatsAppChat } from './WhatsAppContacts';
import { API_BASE_URL } from '../config';

interface LoginProps {
    connectionState: ConnectionState;
    whatsappContacts?: WhatsAppContact[];
    whatsappChats?: WhatsAppChat[];
}

export function QRCodeLogin({ connectionState, whatsappContacts = [], whatsappChats = [] }: LoginProps) {
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [hasClaimed, setHasClaimed] = useState(false);

    // Claim QR session when component mounts and WhatsApp is not connected
    useEffect(() => {
        // Wait for socket to be connected and authenticated before claiming
        const claimSession = () => {
            if (!connectionState.whatsapp && !connectionState.whatsappSyncing && socket.connected && !hasClaimed) {
                console.log('[Login] Claiming QR session... (socket connected:', socket.connected, ')');
                socket.emit('claim-qr-session');
                setHasClaimed(true);
            }
        };

        // Delay initial claim to ensure socket is authenticated
        const initialTimer = setTimeout(claimSession, 500);
        
        // Re-claim when socket reconnects (reset claimed state on disconnect)
        const handleConnect = () => {
            // Delay to allow authentication to complete
            setTimeout(() => {
                if (!connectionState.whatsapp && !connectionState.whatsappSyncing) {
                    console.log('[Login] Socket reconnected, re-claiming QR session...');
                    socket.emit('claim-qr-session');
                    setHasClaimed(true);
                }
            }, 500);
        };

        const handleDisconnect = () => {
            setHasClaimed(false);
        };

        // Listen for QR session errors
        const handleError = (error: { message: string }) => {
            console.error('[Login] QR session error:', error.message);
            setHasClaimed(false); // Allow retry on error
        };
        
        socket.on('connect', handleConnect);
        socket.on('disconnect', handleDisconnect);
        socket.on('error', handleError);
        
        return () => {
            clearTimeout(initialTimer);
            socket.off('connect', handleConnect);
            socket.off('disconnect', handleDisconnect);
            socket.off('error', handleError);
        };
    }, [connectionState.whatsapp, connectionState.whatsappSyncing, hasClaimed]);

    const handleWhatsAppLogout = async () => {
        if (!window.confirm('Are you sure you want to disconnect WhatsApp? You will need to scan the QR code again to reconnect.')) {
            return;
        }
        setIsLoggingOut(true);
        const token = localStorage.getItem('auth_token');
        try {
            const response = await fetch(`${API_BASE_URL}/whatsapp/refresh-qr`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });
            const data = await response.json();
            if (data.success) {
                // Reload page to reset state and show QR code
                setTimeout(() => {
                    window.location.reload();
                }, 1000);
            } else {
                console.error('Failed to logout WhatsApp:', data.message);
                setIsLoggingOut(false);
            }
        } catch (err) {
            console.error('Error logging out WhatsApp:', err);
            setIsLoggingOut(false);
        }
    };

    const handleRefreshQR = async () => {
        setIsRefreshing(true);
        const token = localStorage.getItem('auth_token');
        try {
            const response = await fetch(`${API_BASE_URL}/whatsapp/refresh-qr`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });
            const data = await response.json();
            if (!data.success) {
                console.error('Failed to refresh QR:', data.message);
            }
        } catch (err) {
            console.error('Error refreshing QR:', err);
        } finally {
            // Keep refreshing state for a bit while new QR loads
            setTimeout(() => setIsRefreshing(false), 3000);
        }
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* WhatsApp Connection */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-8 flex flex-col items-center">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center">
                        <MessageCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h2 className="text-xl font-semibold text-white">WhatsApp</h2>
                        <p className="text-sm text-gray-400">Connect your account</p>
                    </div>
                    {connectionState.whatsapp && (
                        <CheckCircle className="text-green-400 ml-2" size={24} />
                    )}
                </div>
                
                {connectionState.whatsapp ? (
                    <div className="w-64 flex flex-col items-center justify-center">
                        <div className="w-full h-48 flex flex-col items-center justify-center bg-green-500/20 rounded-2xl border border-green-500/30 mb-4">
                            <CheckCircle size={48} className="text-green-400 mb-3" />
                            <span className="text-lg font-medium text-green-400">Connected!</span>
                        </div>
                        <button
                            onClick={handleWhatsAppLogout}
                            disabled={isLoggingOut}
                            className="w-full px-4 py-2.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-xl flex items-center justify-center gap-2 text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <LogOut className={`w-4 h-4 ${isLoggingOut ? 'animate-pulse' : ''}`} />
                            {isLoggingOut ? 'Disconnecting...' : 'Disconnect WhatsApp'}
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="bg-white p-4 rounded-xl mb-6 shadow-lg">
                            {connectionState.whatsappQr ? (
                                <QRCodeSVG value={connectionState.whatsappQr} size={220} />
                            ) : (
                                <div className="w-[220px] h-[220px] flex flex-col items-center justify-center text-gray-400">
                                    <Loader2 className="w-8 h-8 animate-spin mb-2" />
                                    <span className="text-sm">Waiting for QR Code...</span>
                                </div>
                            )}
                        </div>
                        <div className="flex items-start gap-3 text-gray-300 text-sm max-w-xs">
                            <Smartphone className="w-5 h-5 flex-shrink-0 mt-0.5 text-gray-400" />
                            <p>Open WhatsApp on your phone → Settings → Linked Devices → Scan QR code</p>
                        </div>
                        <button
                            onClick={handleRefreshQR}
                            disabled={isRefreshing}
                            className="mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 text-gray-300 rounded-lg flex items-center gap-2 text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                            {isRefreshing ? 'Refreshing...' : 'Refresh QR Code'}
                        </button>
                    </>
                )}
            </div>

            {/* Signal Connection */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-8 flex flex-col items-center">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                        <MessageCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h2 className="text-xl font-semibold text-white">Signal</h2>
                        <p className="text-sm text-gray-400">Connect your account</p>
                    </div>
                    {connectionState.signal && (
                        <CheckCircle className="text-blue-400 ml-2" size={24} />
                    )}
                </div>
                
                {connectionState.signal ? (
                    <div className="w-64 h-64 flex flex-col items-center justify-center bg-blue-500/20 rounded-2xl border border-blue-500/30">
                        <CheckCircle size={64} className="text-blue-400 mb-4" />
                        <span className="text-lg font-medium text-blue-400">Connected!</span>
                        <span className="text-sm text-blue-300 mt-2">{connectionState.signalNumber}</span>
                    </div>
                ) : connectionState.signalApiAvailable ? (
                    <>
                        <div className="bg-white p-4 rounded-xl mb-6 shadow-lg">
                            {connectionState.signalQrImage ? (
                                <img
                                    src={connectionState.signalQrImage}
                                    alt="Signal QR Code"
                                    width={220}
                                    height={220}
                                    className="bg-white"
                                />
                            ) : (
                                <div className="w-[220px] h-[220px] flex flex-col items-center justify-center text-gray-400">
                                    <Loader2 className="w-8 h-8 animate-spin mb-2" />
                                    <span className="text-sm">Waiting for QR Code...</span>
                                </div>
                            )}
                        </div>
                        <div className="flex items-start gap-3 text-gray-300 text-sm max-w-xs">
                            <Smartphone className="w-5 h-5 flex-shrink-0 mt-0.5 text-gray-400" />
                            <p>Open Signal on your phone → Settings → Linked Devices → Scan QR code</p>
                        </div>
                    </>
                ) : (
                    <div className="w-64 h-64 flex flex-col items-center justify-center bg-blue-500/10 rounded-2xl border border-blue-500/20">
                        <MessageCircle size={48} className="text-blue-400/50 mb-4" />
                        <p className="text-blue-300 text-center px-4 font-semibold text-lg">Coming Soon</p>
                        <p className="text-xs text-gray-400 text-center px-4 mt-2">
                            Signal integration is under development
                        </p>
                    </div>
                )}
            </div>
        
            {/* Show WhatsApp Contacts when connected */}
            {connectionState.whatsapp && (whatsappContacts.length > 0 || whatsappChats.length > 0) && (
                <div className="col-span-1 md:col-span-2 mt-6">
                    <WhatsAppContacts 
                        contacts={whatsappContacts} 
                        chats={whatsappChats} 
                    />
                </div>
            )}
        </div>
    );
}

// Keep old export for backwards compatibility
export const Login = QRCodeLogin;
